import React, { useId } from "react";
import {
  BatchAvailabilityStrategy,
  RestrictionInfo,
} from "../../../../types";

export const AVERAGED_BADGE_ARIA_LABEL = "Cena uśredniona z całego pobytu";

interface AveragedPriceBadgeProps {
  roomName: string;
  strategy?: BatchAvailabilityStrategy;
  restrictionInfo?: RestrictionInfo;
}

const reasonLabel: Record<
  NonNullable<BatchAvailabilityStrategy["reason"]>,
  string
> = {
  restrictions: "Profitroom wymaga minimum długości pobytu dla tego terminu",
  "api-error": "Profitroom zwrócił błąd dla zapytań per dzień",
  "no-availability": "Brak dostępności w zapytaniach per dzień",
};

const pluralRules = new Intl.PluralRules("pl-PL");

const formatNights = (nights: number): string => {
  const form = pluralRules.select(nights);
  if (form === "one") return `${nights} noc`;
  if (form === "few") return `${nights} noce`;
  return `${nights} nocy`;
};

const formatIsoDate = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return iso;
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const formatTermDates = (term: string): string => {
  const parts = term.split(" - ");
  if (parts.length !== 2) return term;
  return parts.map(formatIsoDate).join(" – ");
};

const formatAmount = (amount: number, currency: string): string =>
  new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    maximumFractionDigits: 0,
  }).format(amount);

const AveragedPriceBadge: React.FC<AveragedPriceBadgeProps> = ({
  roomName,
  strategy,
  restrictionInfo,
}) => {
  const tooltipId = useId();

  if (!strategy || strategy.type === "daily") {
    return null;
  }

  const roomPricing = restrictionInfo?.profitroomData?.roomPricing?.[roomName];
  const totalNights = restrictionInfo?.profitroomData?.totalNights;
  const requestedTerm = restrictionInfo?.profitroomData?.requestedTerm;
  const actualTerm = restrictionInfo?.profitroomData?.actualTerm;
  const reasonText = strategy.reason ? reasonLabel[strategy.reason] : undefined;

  const totalPrice = roomPricing?.totalPriceFromProfitroom;
  const perNight = roomPricing?.pricePerNightCalculated;
  const currency = roomPricing?.currency ?? "PLN";

  const hasPriceBreakdown =
    totalPrice !== undefined &&
    perNight !== undefined &&
    typeof totalNights === "number" &&
    totalNights > 0;

  const hasTerm = Boolean(requestedTerm && actualTerm);
  const isExtendedTerm = hasTerm && requestedTerm !== actualTerm;

  return (
    <span className="relative inline-flex items-center group align-middle ml-1">
      <button
        type="button"
        aria-label={AVERAGED_BADGE_ARIA_LABEL}
        aria-describedby={tooltipId}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold leading-none border border-amber-300 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        ø
      </button>

      <div
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block group-focus-within:block z-50 w-72 p-3 rounded-md bg-gray-900 text-white text-xs shadow-lg"
      >
        <div className="font-semibold mb-1.5">Cena uśredniona</div>

        {hasPriceBreakdown ? (
          <div className="mb-2 leading-snug">
            Cena pobrana łącznie za pobyt:{" "}
            <span className="font-semibold">
              {formatAmount(totalPrice!, currency)}
            </span>{" "}
            i podzielona przez {formatNights(totalNights!)} ={" "}
            <span className="font-semibold">
              {formatAmount(perNight!, currency)}/noc
            </span>
            .
          </div>
        ) : (
          <div className="mb-2 leading-snug">
            Cena pokazana to średnia z całego pobytu — Profitroom nie
            udostępnia indywidualnych cen dla tego terminu.
          </div>
        )}

        {hasTerm && (
          <div className="mb-2 leading-snug">
            {isExtendedTerm ? (
              <>
                Termin pobytu:{" "}
                <span className="font-mono text-[11px]">
                  {formatTermDates(requestedTerm!)}
                </span>
                <br />
                Termin pobrany z Profitroom:{" "}
                <span className="font-mono text-[11px]">
                  {formatTermDates(actualTerm!)}
                </span>{" "}
                (rozszerzony, by spełnić restrykcję min. pobytu).
              </>
            ) : (
              <>
                Termin:{" "}
                <span className="font-mono text-[11px]">
                  {formatTermDates(requestedTerm!)}
                </span>
              </>
            )}
          </div>
        )}

        {reasonText && (
          <div className="text-gray-300 leading-snug">{reasonText}.</div>
        )}

        <div
          className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 -mt-1 rotate-45 bg-gray-900"
          aria-hidden="true"
        />
      </div>
    </span>
  );
};

export default AveragedPriceBadge;
